import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Calendar } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = 'February 6, 2025';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-card border-b border-border sticky top-0 z-30 backdrop-blur bg-card/95"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-semibold">Privacy Policy</h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  <Calendar className="h-4 w-4" />
                  <span>Last updated: {lastUpdated}</span>
                </div>
              </div>
            </div>
            <Button onClick={() => navigate(-1)} variant="outline" size="sm">
              Back
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="p-6 sm:p-8 prose prose-sm sm:prose max-w-none dark:prose-invert">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                At VehicleBuddy, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our vehicle management platform. Please read this privacy policy carefully.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-2">Personal Information</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Name and email address</li>
                <li>Phone number (optional)</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Account credentials</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">Vehicle Information</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You may choose to provide:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Vehicle registration numbers</li>
                <li>VIN (Vehicle Identification Number)</li>
                <li>Make, model, and year of vehicles</li>
                <li>MOT, tax, and insurance expiry dates</li>
                <li>Mileage and fuel logs</li>
                <li>Maintenance records and expense data</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">Document and Image Data</h3>
              <p className="text-muted-foreground leading-relaxed">
                When you use our AI document scanning feature, we process images of your documents (insurance certificates, V5C forms, MOT certificates, etc.) to extract relevant data. These images are stored securely in our cloud storage.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Device information and browser type</li>
                <li>IP address and location data</li>
                <li>Usage statistics and interaction data</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">3. How We Use Your Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Process your transactions and manage subscriptions</li>
                <li>Send you reminders and notifications you've requested</li>
                <li>Respond to your comments and questions</li>
                <li>Detect, prevent, and address technical issues and fraud</li>
                <li>Analyze usage patterns to improve our AI models</li>
                <li>Send you marketing communications (with your consent)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">4. AI Document Scanning</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Our AI document scanning feature uses advanced machine learning models to extract information from uploaded documents. Please note:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Document images are processed using secure AI services</li>
                <li>Extracted data is stored in our database</li>
                <li>Original document images are stored in encrypted cloud storage</li>
                <li>We do not share document images with third parties except as required to provide the service</li>
                <li>You can delete your documents at any time through your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">5. Information Sharing and Disclosure</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We do not sell your personal information. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Service Providers:</strong> We use third-party services including Stripe (payments), cloud storage providers, and AI service providers</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">6. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We implement appropriate technical and organizational measures to protect your information:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Data encryption in transit (HTTPS/TLS)</li>
                <li>Encrypted data storage for sensitive information</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication</li>
                <li>Secure password hashing (bcrypt)</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                However, no method of transmission or storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">7. Your Rights and Choices</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Data Portability:</strong> Request your data in a portable format</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where applicable</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                To exercise these rights, please contact us at hello@vehiclebuddy.co.uk
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">8. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain your information for as long as necessary to provide our services and comply with legal obligations. When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required by law to retain it longer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">9. Cookies and Tracking</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Keep you logged in to your account</li>
                <li>Remember your preferences</li>
                <li>Analyze usage and improve our services</li>
                <li>Provide personalized content</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                You can control cookies through your browser settings, but disabling cookies may affect functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">10. Third-Party Links</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our Service may contain links to third-party websites. We are not responsible for the privacy practices of these websites. We encourage you to read their privacy policies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">11. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our Service is not intended for users under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">12. International Data Transfers</h2>
              <p className="text-muted-foreground leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">13. Changes to This Privacy Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by email or through a notice on our Service. Your continued use after such notification constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">14. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                If you have questions or concerns about this Privacy Policy, please contact us:
              </p>
              <p className="text-muted-foreground">
                Email: <a href="mailto:hello@vehiclebuddy.co.uk" className="text-primary hover:underline">hello@vehiclebuddy.co.uk</a>
              </p>
              <p className="text-muted-foreground mt-2">
                Company: VehicleBuddy Ltd<br />
                Location: United Kingdom
              </p>
            </section>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
